import React, { useEffect, useMemo, useState } from "react";
import {
  Box, Typography, Button, Paper, Stack, IconButton, Chip, TextField,
  InputAdornment, Table, TableHead, TableBody, TableRow, TableCell, Tooltip,
  Popover, Checkbox, Divider, CircularProgress,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import CommonBreadcrumbs from "../common-breadcrumbs/CommonBreadcrumbs";
import ROUTE_PATHS from "../main-layout/RoutesPath";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowRightAltIcon from "@mui/icons-material/ArrowRightAlt";
import CompareArrowsOutlinedIcon from "@mui/icons-material/CompareArrowsOutlined";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import ChecklistRtlOutlinedIcon from "@mui/icons-material/ChecklistRtlOutlined";
import ViewColumnOutlinedIcon from "@mui/icons-material/ViewColumnOutlined";
import GavelOutlinedIcon from "@mui/icons-material/GavelOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import FingerprintOutlinedIcon from "@mui/icons-material/FingerprintOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import ScheduleIcon from "@mui/icons-material/Schedule";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import {
  TONES, cellFor, DIFF_STATES, diagnoseCell, cellKey, countRowDispositions,
  SEGMENTS, segmentCount, inSegment, applyDedupe,
  applyColumnFilters, applySort, distinctValues, applyAskFilter,
} from "./reconcileModel";
import CellResolutionPopover from "./CellResolutionPopover";
import ColumnHeaderMenu from "./ColumnHeaderMenu";
import AskPanel from "./AskPanel";
import TeachFieldPopover from "./TeachFieldPopover";
import { askAboutRunApi, suggestRosterRulesApi } from "../../services/api/rosterComparisonApi";

const DARK = "#344049";
const MUTED = "#98A2B3";

const csvEscape = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
function downloadCsv(filename, rows) {
  const csv = rows.map((r) => r.map(csvEscape).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

// v2 look: flat/borderless when inactive, a solid purple pill when active, with a
// leading tone dot. Used across the reconcile toolbar (segments, corrections, SHOW…).
function Pill({ active, dot, onClick, children }) {
  return (
    <Button
      onClick={onClick} disableElevation
      sx={{
        px: active ? 1.5 : 1.1, py: 0.4, minWidth: 0, borderRadius: "999px", fontSize: "0.8rem",
        fontWeight: active ? 600 : 500, color: active ? "#fff" : "#475467",
        backgroundColor: active ? "#635BFF" : "transparent", border: "1px solid transparent",
        "&:hover": { backgroundColor: active ? "#5850EC" : "#F2F4F7" }, textTransform: "none",
        whiteSpace: "nowrap",
      }}
    >
      {dot && <Box component="span" sx={{ width: 7, height: 7, borderRadius: "50%", backgroundColor: active ? "rgba(255,255,255,0.85)" : dot, mr: 0.75, display: "inline-block" }} />}
      {children}
    </Button>
  );
}

/* --------------------------- cell tone + rendering -------------------------- */
function cellTone(cell) {
  if (cell.s === "artifact") return "slate";
  if (cell.s === "future") return "sky";
  if (cell.s === "source_only" || cell.s === "target_only") return "sky";
  const wentBlank = (cell.l && !cell.r) || (!cell.l && cell.r);
  return wentBlank ? "amber" : "rose";
}

function GlyphFor({ cell }) {
  const t = TONES[cellTone(cell)];
  if (cell.s === "artifact") return <AutorenewIcon sx={{ fontSize: 14, color: t.fg }} />;
  if (cell.s === "future") return <ScheduleIcon sx={{ fontSize: 14, color: t.fg }} />;
  return <WarningAmberIcon sx={{ fontSize: 14, color: t.fg }} />;
}

function InlineDiff({ cell }) {
  const t = TONES[cellTone(cell)];
  return (
    <Box sx={{ backgroundColor: t.bg, border: `1px solid ${t.ring}`, borderRadius: "6px", px: 0.75, py: 0.5, minWidth: 0 }}>
      <Typography sx={{ fontSize: "0.76rem", color: MUTED, textDecoration: "line-through", lineHeight: 1.3, wordBreak: "break-word" }}>
        {cell.l || "blank"}
      </Typography>
      <Stack direction="row" spacing={0.25} alignItems="flex-start">
        <ArrowRightAltIcon sx={{ fontSize: 15, color: t.fg, mt: "1px", flexShrink: 0 }} />
        <Typography sx={{ fontSize: "0.8rem", color: t.fg, fontWeight: 600, lineHeight: 1.3, wordBreak: "break-word" }}>
          {cell.r || "blank"}
        </Typography>
      </Stack>
    </Box>
  );
}

function ResolvedInline({ cell, dispo }) {
  const review = dispo.verdict === "review";
  const t = review ? TONES.amber : TONES.emerald;
  return (
    <Box sx={{ backgroundColor: t.bg, border: `1px solid ${t.ring}`, borderRadius: "6px", px: 0.75, py: 0.5 }}>
      <Typography sx={{ fontSize: "0.72rem", color: MUTED, textDecoration: "line-through", lineHeight: 1.2 }}>
        {cell.l || "blank"} → {cell.r || "blank"}
      </Typography>
      <Typography sx={{ fontSize: "0.78rem", color: t.fg, fontWeight: 600, lineHeight: 1.3 }}>
        {review ? "In review" : `Kept · ${dispo.value ?? "—"}`}
      </Typography>
    </Box>
  );
}

/* ------------------------------- Reconciliation ------------------------------ */
function ReconciliationView({ result, categories, cat, setCat, askFilter, rowDetail, bothShowsPrimeOnly, historyByNpi, reasonRenderer }) {
  const { columns, providers, run } = result;
  const [segment, setSegment] = useState("both");
  const [correction, setCorrection] = useState("all");
  const [bothSub, setBothSub] = useState("all");
  const [dedupe, setDedupe] = useState("none");
  const [onlyDiffCols, setOnlyDiffCols] = useState(false);
  const [search, setSearch] = useState("");
  const [dispo, setDispo] = useState(() => new Map());
  const [pop, setPop] = useState(null);
  const [sort, setSort] = useState(null);
  const [colFilters, setColFilters] = useState({});
  const [hiddenCols, setHiddenCols] = useState(() => new Set());
  const [expanded, setExpanded] = useState(() => new Set());
  const [colsAnchor, setColsAnchor] = useState(null);
  const [askExtra, setAskExtra] = useState(null);

  const dataCols = columns.filter((c) => c !== "NPI");
  const srcLabel = run.source.title;
  const tgtLabel = run.target.title;
  const seg = SEGMENTS.find((s) => s.id === segment) || SEGMENTS[0];
  // Reconcile shows the PRIME (target) values only for "present in both" rows — no
  // source-vs-target diff. Render those cells raw from the target side ("r").
  const primeBoth = !!bothShowsPrimeOnly && segment === "both";
  const rawSide = primeBoth ? "r" : (seg.raw && segment !== "both" ? seg.raw : null);

  // apply a question's filter when it arrives
  useEffect(() => {
    if (!askFilter) return;
    setSegment(askFilter.segment || "both");
    setCorrection(askFilter.verdict || "all");
    setBothSub("all");
    setDedupe("none");
    setSearch("");
    setAskExtra({ fields: askFilter.fields || [], onlyDifferences: !!askFilter.onlyDifferences, text: askFilter.text || "" });
  }, [askFilter]);

  const inCat = useMemo(
    () => providers.filter((p) => (categories[0] === "All" ? true : p.changeType === cat)),
    [providers, cat, categories]
  );

  const segStats = useMemo(() => {
    const s = { matched: 0, sourceOnly: 0, targetOnly: 0 };
    inCat.forEach((p) => {
      if (p.status === "matched") s.matched++;
      else if (p.status === "source_only") s.sourceOnly++;
      else if (p.status === "target_only") s.targetOnly++;
    });
    return s;
  }, [inCat]);

  const bothBase = useMemo(() => inCat.filter((p) => inSegment(p, "both")), [inCat]);
  const andCounts = useMemo(() => ({
    same: bothBase.filter((p) => p.category === "exact").length,
    differ: bothBase.filter((p) => p.category !== "exact").length,
  }), [bothBase]);

  const setDisposition = (key, d) => setDispo((prev) => { const m = new Map(prev); m.set(key, d); return m; });
  const revertDisposition = (key) => setDispo((prev) => { const m = new Map(prev); m.delete(key); return m; });

  const segRows = useMemo(() => inCat.filter((p) => inSegment(p, segment)), [inCat, segment]);

  const bothRows = useMemo(() => {
    if (segment !== "both") return segRows;
    if (bothSub === "exact") return segRows.filter((p) => p.category === "exact");
    if (bothSub === "differ") return segRows.filter((p) => p.category !== "exact");
    return segRows;
  }, [segRows, segment, bothSub]);

  const correctionRows = useMemo(() => {
    if (segment !== "both" || correction === "all") return bothRows;
    return bothRows.filter((p) => {
      const c = countRowDispositions(p, dataCols, dispo);
      if (correction === "differences") return c.open > 0;
      if (correction === "corrected") return c.corrected > 0;
      if (correction === "review") return c.review > 0;
      return true;
    });
  }, [bothRows, segment, correction, dataCols, dispo]);

  const askRows = useMemo(() => {
    if (!askExtra) return correctionRows;
    return applyAskFilter(correctionRows, { fields: askExtra.fields, onlyDifferences: askExtra.onlyDifferences, text: askExtra.text }, dataCols, dispo);
  }, [correctionRows, askExtra, dataCols, dispo]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    let out = applyColumnFilters(askRows, colFilters);
    out = applyDedupe(out, dedupe);
    out = out.filter((p) => !q || (p.name || "").toLowerCase().includes(q) || (p.npi || "").includes(q));
    out = applySort(out, sort);
    return out;
  }, [askRows, colFilters, dedupe, search, sort]);

  const correctionCounts = useMemo(() => {
    let corrected = 0, review = 0, differences = 0;
    bothRows.forEach((p) => {
      const c = countRowDispositions(p, dataCols, dispo);
      if (c.corrected > 0) corrected++;
      if (c.review > 0) review++;
      if (c.open > 0) differences++;
    });
    return { corrected, review, differences };
  }, [bothRows, dataCols, dispo]);

  // "Reason" is shown as a dedicated column right after Provider, so keep it out of the
  // scrolling data columns. The fixed (sticky) columns are NPI + Provider (+ Reason).
  const reasonInData = dataCols.includes("Reason");
  // Show the Reason column when the roster carries it OR the cycle analysis can supply it.
  const hasHistory = !!(historyByNpi && Object.keys(historyByNpi).length);
  const showReason = reasonInData || hasHistory;
  const fixedCols = showReason ? 3 : 2;
  const displayCols = useMemo(() => {
    let cols = dataCols.filter((c) => c !== "Reason" && !hiddenCols.has(c));
    if (onlyDiffCols && !rawSide) {
      const keep = cols.filter((col) => rows.some((p) => DIFF_STATES.has(cellFor(p, col).s)));
      if (keep.length) cols = keep;
    }
    return cols;
  }, [dataCols, hiddenCols, onlyDiffCols, rawSide, rows]);

  // The reason for a provider: prefer the PRIME roster's Reason field; when the roster
  // doesn't carry it, fall back to the reason recorded in the cycle's analysis JSON.
  const reasonText = (p) => {
    const c = cellFor(p, "Reason");
    const fromRoster = (rawSide ? c[rawSide] : (c.l || c.r)) || "";
    if (fromRoster.trim()) return fromRoster;
    return historyByNpi?.[String(p.npi)]?.change || "";
  };

  const toggleExpand = (id) => setExpanded((prev) => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });
  const toggleHidden = (c) => setHiddenCols((prev) => { const s = new Set(prev); s.has(c) ? s.delete(c) : s.add(c); return s; });

  const openPopover = (e, p, col, cell) => {
    const diag = diagnoseCell(col, cell, srcLabel, tgtLabel);
    const key = cellKey(p.npi, col);
    setPop({
      anchorEl: e.currentTarget,
      ctx: {
        field: col, cell, diag, srcLabel, tgtLabel, npi: p.npi,
        disposition: dispo.get(key),
        onResolve: (d) => { setDisposition(key, d); setPop(null); },
        onRevert: () => { revertDisposition(key); setPop(null); },
      },
    });
  };

  const exportCsv = () => {
    const header = ["NPI", "Provider", "Segment", ...displayCols];
    const out = [header];
    rows.forEach((p) => {
      out.push([p.npi, p.name, segment, ...displayCols.map((c) => {
        const cell = cellFor(p, c);
        return rawSide ? cell[rawSide] : (DIFF_STATES.has(cell.s) ? `${cell.l} → ${cell.r}` : cell.l || cell.r);
      })]);
    });
    downloadCsv(`comparison-${run.hash}.csv`, out);
  };

  const StatusChip = ({ p }) => {
    if (p.status === "source_only") return <Chip label="Only in customer" size="small" sx={{ height: 18, fontSize: "0.66rem", backgroundColor: TONES.sky.bg, color: TONES.sky.fg }} />;
    if (p.status === "target_only") return <Chip label="Only in PRIME" size="small" sx={{ height: 18, fontSize: "0.66rem", backgroundColor: TONES.sky.bg, color: TONES.sky.fg }} />;
    // Reconcile "present in both": show the PRIME record without a diff verdict.
    if (primeBoth) return <Chip label="In PRIME roster" size="small" sx={{ height: 18, fontSize: "0.66rem", backgroundColor: TONES.sky.bg, color: TONES.sky.fg }} />;
    if (p.category === "exact") return <Chip label="Exact match" size="small" sx={{ height: 18, fontSize: "0.66rem", backgroundColor: TONES.emerald.bg, color: TONES.emerald.fg }} />;
    const c = countRowDispositions(p, dataCols, dispo);
    const changed = c.open + c.corrected + c.review;
    return (
      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
        <Chip label={`${changed} field ${changed === 1 ? "value is" : "values are"} different`} size="small" sx={{ height: 18, fontSize: "0.66rem", backgroundColor: TONES.rose.bg, color: TONES.rose.fg }} />
        {c.corrected > 0 && <Chip label={`${c.corrected} corrected`} size="small" sx={{ height: 18, fontSize: "0.66rem", backgroundColor: TONES.emerald.bg, color: TONES.emerald.fg }} />}
        {c.review > 0 && <Chip label={`${c.review} in review`} size="small" sx={{ height: 18, fontSize: "0.66rem", backgroundColor: TONES.amber.bg, color: TONES.amber.fg }} />}
      </Stack>
    );
  };

  const renderCell = (p, col) => {
    const cell = cellFor(p, col);
    if (rawSide) {
      const val = cell[rawSide];
      return (
        <TableCell key={col} sx={{ py: 1, borderBottom: "1px solid #F0F1F3", verticalAlign: "top", whiteSpace: "nowrap" }}>
          <Typography sx={{ fontSize: "0.82rem", color: val ? DARK : MUTED, fontStyle: val ? "normal" : "italic" }}>{val || "blank"}</Typography>
        </TableCell>
      );
    }
    if (DIFF_STATES.has(cell.s)) {
      const key = cellKey(p.npi, col);
      const d = dispo.get(key);
      return (
        <TableCell key={col} sx={{ py: 0.75, borderBottom: "1px solid #F0F1F3", verticalAlign: "top", minWidth: 150 }}>
          <Box onClick={(e) => openPopover(e, p, col, cell)} sx={{ position: "relative", cursor: "pointer", "&:hover": { opacity: 0.88 } }}>
            <Box sx={{ position: "absolute", top: 2, right: 2, zIndex: 1 }}>
              {d ? <CheckCircleIcon sx={{ fontSize: 14, color: (d.verdict === "review" ? TONES.amber : TONES.emerald).fg }} /> : <GlyphFor cell={cell} />}
            </Box>
            {d ? <ResolvedInline cell={cell} dispo={d} /> : <InlineDiff cell={cell} />}
          </Box>
        </TableCell>
      );
    }
    const val = cell.l || cell.r;
    return (
      <TableCell key={col} sx={{ py: 1, borderBottom: "1px solid #F0F1F3", verticalAlign: "top", whiteSpace: "nowrap" }}>
        <Typography sx={{ fontSize: "0.82rem", color: val ? "#667085" : MUTED, fontStyle: val ? "normal" : "italic" }}>{val || "blank"}</Typography>
      </TableCell>
    );
  };

  const RowDrawer = ({ p }) => {
    const singleSided = p.status === "source_only" || p.status === "target_only";
    const sourcePresent = p.status !== "target_only";
    const targetPresent = p.status !== "source_only";
    // Single-sided records have no field-level "diffs"; show every field so the
    // present side's values are all visible (the other side reads "not in this file").
    const [showAll, setShowAll] = useState(singleSided);
    const fields = showAll ? dataCols : dataCols.filter((c) => DIFF_STATES.has(cellFor(p, c).s));
    const list = fields.length ? fields : dataCols;
    const naCell = <Typography sx={{ fontSize: "0.8rem", color: "#CBD2D9", fontStyle: "italic" }}>not in this file</Typography>;
    return (
      <TableRow>
        <TableCell colSpan={displayCols.length + fixedCols} sx={{ backgroundColor: "#FAFBFC", borderBottom: "2px solid #EDEFF2", p: 1.5 }}>
          <Box sx={{ width: "80vw", maxWidth: "80vw", position: "sticky", left: 12 }}>
          {rowDetail ? rowDetail(p) : (
          <>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <Typography sx={{ fontSize: "0.78rem", fontWeight: 600, color: DARK }}>
              {singleSided
                ? `All ${list.length} fields · only in the ${p.status === "source_only" ? "customer" : "PRIME"} roster`
                : showAll ? `All ${list.length} fields` : `Showing ${list.length} differing ${list.length === 1 ? "field" : "fields"}`}
            </Typography>
            {!singleSided && (
              <Button size="small" onClick={() => setShowAll((v) => !v)} sx={{ textTransform: "none", fontSize: "0.75rem", color: "#3E7BFA" }}>
                {showAll ? "Show differing only" : "Show all fields"}
              </Button>
            )}
          </Stack>
          <Table size="small" sx={{ backgroundColor: "#fff", borderRadius: "8px", "& td,& th": { borderColor: "#F0F1F3" } }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: "28%" }}><Typography sx={{ fontSize: "0.66rem", fontWeight: 600, color: MUTED, textTransform: "uppercase" }}>Field</Typography></TableCell>
                <TableCell><Typography sx={{ fontSize: "0.66rem", fontWeight: 600, color: MUTED, textTransform: "uppercase" }}>{srcLabel}</Typography></TableCell>
                <TableCell><Typography sx={{ fontSize: "0.66rem", fontWeight: 600, color: MUTED, textTransform: "uppercase" }}>{tgtLabel}</Typography></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {list.map((c) => {
                const cell = cellFor(p, c);
                const diff = DIFF_STATES.has(cell.s);
                const t = diff ? TONES[cellTone(cell)] : null;
                return (
                  <TableRow key={c} sx={{ backgroundColor: diff ? t.bg : "transparent" }}>
                    <TableCell><Typography sx={{ fontSize: "0.8rem", color: DARK, fontWeight: diff ? 600 : 400 }}>{c}</Typography></TableCell>
                    <TableCell>{sourcePresent ? <Typography sx={{ fontSize: "0.8rem", color: cell.l ? (diff ? DARK : "#667085") : MUTED, fontStyle: cell.l ? "normal" : "italic" }}>{cell.l || "blank"}</Typography> : naCell}</TableCell>
                    <TableCell>{targetPresent ? <Typography sx={{ fontSize: "0.8rem", color: cell.r ? (diff ? t.fg : "#667085") : MUTED, fontStyle: cell.r ? "normal" : "italic", fontWeight: diff ? 600 : 400 }}>{cell.r || "blank"}</Typography> : naCell}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          </>
          )}
          </Box>
        </TableCell>
      </TableRow>
    );
  };

  return (
    <Box>
      {categories[0] !== "All" && (
        <Stack direction="row" spacing={0} sx={{ backgroundColor: DARK, borderRadius: "8px 8px 0 0", px: 1, pt: 1, flexWrap: "wrap" }}>
          {categories.map((c) => (
            <Box key={c} onClick={() => { setCat(c); setSegment("both"); setCorrection("all"); setBothSub("all"); setDedupe("none"); setAskExtra(null); }}
              sx={{ px: 2, py: 1, cursor: "pointer", fontSize: "0.85rem", fontWeight: 500, color: c === cat ? DARK : "rgba(255,255,255,0.85)", backgroundColor: c === cat ? "#fff" : "transparent", borderRadius: "6px 6px 0 0" }}>
              {c}
            </Box>
          ))}
        </Stack>
      )}

      <Paper elevation={0} sx={{ border: "1px solid #E6E8EB", borderTop: categories[0] !== "All" ? "none" : undefined, borderRadius: categories[0] !== "All" ? "0 0 8px 8px" : "8px", p: 2 }}>
        {/* segment pills */}
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
          {SEGMENTS.map((s) => (
            <Pill key={s.id} active={segment === s.id} dot={TONES[s.tone].dot}
              onClick={() => { setSegment(s.id); setCorrection("all"); setBothSub("all"); setDedupe("none"); }}>
              {s.label}&nbsp;<b>{segmentCount(s.id, segStats)}</b>
            </Pill>
          ))}
        </Stack>

        {/* correction filters + only-diff + columns + export (comparison segment only) */}
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            {segment === "both" && !primeBoth && (
              <>
                <Pill active={correction === "all"} onClick={() => setCorrection("all")}>All</Pill>
                <Pill active={correction === "differences"} dot={TONES.rose.dot} onClick={() => setCorrection("differences")}>Differences&nbsp;<b>{correctionCounts.differences}</b></Pill>
                <Pill active={correction === "corrected"} dot={TONES.emerald.dot} onClick={() => setCorrection("corrected")}>Corrected&nbsp;<b>{correctionCounts.corrected}</b></Pill>
                <Pill active={correction === "review"} dot={TONES.amber.dot} onClick={() => setCorrection("review")}>In review&nbsp;<b>{correctionCounts.review}</b></Pill>
              </>
            )}
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            {!rawSide && <Pill active={onlyDiffCols} onClick={() => setOnlyDiffCols((v) => !v)}>Only differences</Pill>}
            <Button onClick={(e) => setColsAnchor(e.currentTarget)} startIcon={<ViewColumnOutlinedIcon sx={{ fontSize: 16 }} />} sx={{ color: DARK, border: "1px solid #E6E8EB", borderRadius: "8px", textTransform: "none", fontSize: "0.82rem" }}>Columns</Button>
            <Tooltip title="Export CSV"><IconButton onClick={exportCsv} sx={{ border: "1px solid #E6E8EB", borderRadius: "8px" }}><FileDownloadOutlinedIcon sx={{ fontSize: 18, color: DARK }} /></IconButton></Tooltip>
          </Stack>
        </Stack>

        {/* SHOW (dedupe) + search */}
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography sx={{ fontSize: "0.72rem", color: MUTED, fontWeight: 600, mr: 0.5 }}>SHOW</Typography>
            <Pill active={dedupe === "none"} onClick={() => setDedupe("none")}>All&nbsp;<b>{rows.length}</b></Pill>
            <Pill active={dedupe === "npi"} onClick={() => setDedupe("npi")}>Unique NPIs&nbsp;<b>{applyDedupe(askRows, "npi").length}</b></Pill>
            <Pill active={dedupe === "npi_location"} onClick={() => setDedupe("npi_location")}>NPI + Location&nbsp;<b>{applyDedupe(askRows, "npi_location").length}</b></Pill>
          </Stack>
          <TextField size="small" placeholder="Search name, NPI, license…" value={search} onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 260, backgroundColor: "#fff", "& .MuiOutlinedInput-root": { borderRadius: "999px" } }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: MUTED }} /></InputAdornment> }} />
        </Stack>

        {/* AND (both only) */}
        {segment === "both" && !primeBoth && (
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
            <Typography sx={{ fontSize: "0.72rem", color: MUTED, fontWeight: 600, mr: 0.5 }}>AND</Typography>
            <Pill active={bothSub === "exact"} dot={TONES.emerald.dot} onClick={() => setBothSub((v) => (v === "exact" ? "all" : "exact"))}>Records with all values are same between rosters&nbsp;<b>{andCounts.same}</b></Pill>
            <Pill active={bothSub === "differ"} dot={TONES.rose.dot} onClick={() => setBothSub((v) => (v === "differ" ? "all" : "differ"))}>Records with some values are different between rosters&nbsp;<b>{andCounts.differ}</b></Pill>
          </Stack>
        )}

        {/* ask-filter banner */}
        {askExtra && (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1, backgroundColor: TONES.indigo.bg, border: `1px solid ${TONES.indigo.ring}`, borderRadius: "8px", px: 1.25, py: 0.75 }}>
            <ChatBubbleOutlineIcon sx={{ fontSize: 16, color: TONES.indigo.fg }} />
            <Typography sx={{ fontSize: "0.78rem", color: TONES.indigo.fg, flex: 1 }}>Filtered by your question.</Typography>
            <Button size="small" onClick={() => setAskExtra(null)} startIcon={<ClearIcon sx={{ fontSize: 14 }} />} sx={{ textTransform: "none", fontSize: "0.75rem", color: TONES.indigo.fg }}>Clear</Button>
          </Stack>
        )}

        {/* grid */}
        <Box sx={{ overflowX: "auto", border: "1px solid #F0F1F3", borderRadius: "8px" }}>
          <Table size="small" sx={{ minWidth: 900 }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: "#F7F8FA" }}>
                <TableCell sx={{ position: "sticky", left: 0, backgroundColor: "#F7F8FA", zIndex: 2, minWidth: 140 }}>
                  <ColumnHeaderMenu label="NPI" col="__npi" sort={sort} onSort={setSort} filter={colFilters["__npi"]} onFilter={(c, f) => setColFilters((p) => ({ ...p, [c]: f }))} distinct={distinctValues(askRows, "__npi")} />
                </TableCell>
                <TableCell sx={{ position: "sticky", left: 140, backgroundColor: "#F7F8FA", zIndex: 2, minWidth: 200 }}>
                  <ColumnHeaderMenu label="Provider" col="__name" sort={sort} onSort={setSort} filter={colFilters["__name"]} onFilter={(c, f) => setColFilters((p) => ({ ...p, [c]: f }))} distinct={distinctValues(askRows, "__name")} />
                </TableCell>
                {showReason && (
                  <TableCell sx={{ minWidth: 200 }}>
                    <ColumnHeaderMenu label="Reason" col="Reason" sort={sort} onSort={setSort} filter={colFilters["Reason"]} onFilter={(cc, f) => setColFilters((p) => ({ ...p, [cc]: f }))} distinct={distinctValues(askRows, "Reason")} />
                  </TableCell>
                )}
                {displayCols.map((c) => (
                  <TableCell key={c}>
                    <ColumnHeaderMenu label={c} col={c} sort={sort} onSort={setSort} filter={colFilters[c]} onFilter={(cc, f) => setColFilters((p) => ({ ...p, [cc]: f }))} distinct={distinctValues(askRows, c)} />
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((p) => {
                const isOpen = expanded.has(p.id);
                return (
                  <React.Fragment key={p.id}>
                    <TableRow sx={{ "&:hover": { backgroundColor: "#FCFCFD" } }}>
                      <TableCell sx={{ verticalAlign: "top", position: "sticky", left: 0, backgroundColor: "#fff", zIndex: 1, borderBottom: "1px solid #F0F1F3", minWidth: 140 }}>
                        <Stack direction="row" spacing={0.5} alignItems="flex-start">
                          <IconButton size="small" onClick={() => toggleExpand(p.id)} sx={{ p: 0.25, mt: "-2px" }}>
                            {isOpen ? <KeyboardArrowDownIcon sx={{ fontSize: 18, color: MUTED }} /> : <KeyboardArrowRightIcon sx={{ fontSize: 18, color: MUTED }} />}
                          </IconButton>
                          <Typography sx={{ fontSize: "0.78rem", color: "#3E7BFA", fontWeight: 600 }}>{p.npi || "no NPI"}</Typography>
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ verticalAlign: "top", position: "sticky", left: 140, backgroundColor: "#fff", zIndex: 1, borderBottom: "1px solid #F0F1F3", minWidth: 200 }}>
                        <Typography sx={{ fontSize: "0.85rem", color: DARK, fontWeight: 500, mb: 0.5 }}>{p.name}</Typography>
                        <StatusChip p={p} />
                      </TableCell>
                      {showReason && (
                        <TableCell sx={{ verticalAlign: "top", borderBottom: "1px solid #F0F1F3", minWidth: 200 }}>
                          {reasonRenderer
                            ? (reasonRenderer(p, reasonText(p)) || <Typography sx={{ fontSize: "0.82rem", color: MUTED }}>—</Typography>)
                            : <Typography sx={{ fontSize: "0.82rem", color: "#667085" }}>{reasonText(p) || "—"}</Typography>}
                        </TableCell>
                      )}
                      {displayCols.map((c) => renderCell(p, c))}
                    </TableRow>
                    {isOpen && <RowDrawer p={p} />}
                  </React.Fragment>
                );
              })}
              {rows.length === 0 && (
                <TableRow><TableCell colSpan={displayCols.length + fixedCols} sx={{ textAlign: "center", py: 5, border: 0 }}><Typography sx={{ color: MUTED }}>No providers match this filter.</Typography></TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      </Paper>

      {/* Columns show/hide */}
      <Popover open={Boolean(colsAnchor)} anchorEl={colsAnchor} onClose={() => setColsAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }} transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ sx: { borderRadius: "10px", border: "1px solid #E6E8EB", p: 1, maxHeight: 360 } }}>
        <Typography sx={{ fontSize: "0.72rem", fontWeight: 600, color: MUTED, textTransform: "uppercase", px: 1, py: 0.5 }}>Columns</Typography>
        {dataCols.filter((c) => c !== "Reason").map((c) => (
          <Stack key={c} direction="row" alignItems="center" onClick={() => toggleHidden(c)} sx={{ px: 0.5, cursor: "pointer", "&:hover": { backgroundColor: "#F7F8FA" }, borderRadius: "6px" }}>
            <Checkbox size="small" checked={!hiddenCols.has(c)} />
            <Typography sx={{ fontSize: "0.8rem", color: DARK }}>{c}</Typography>
          </Stack>
        ))}
      </Popover>

      <CellResolutionPopover open={Boolean(pop)} anchorEl={pop?.anchorEl} ctx={pop?.ctx} onClose={() => setPop(null)} />
    </Box>
  );
}

/* ---------------------------------- Review ---------------------------------- */
function ReviewView({ result }) {
  const { providers } = result;
  const buckets = useMemo(() => {
    const list = [{ id: "all", label: "All findings", sub: "Everything in this bucket", count: providers.filter((p) => p.category !== "exact").length }];
    const so = providers.filter((p) => p.status === "source_only").length;
    const to = providers.filter((p) => p.status === "target_only").length;
    if (so) list.push({ id: "only_source", label: `${so} records only in the customer roster`, count: so });
    if (to) list.push({ id: "only_compare", label: `${to} records only in the PRIME roster`, count: to });
    const byField = {};
    providers.forEach((p) => Object.keys(p.diffs || {}).forEach((f) => { byField[f] = (byField[f] || 0) + 1; }));
    Object.entries(byField).sort((a, b) => b[1] - a[1]).forEach(([f, n]) =>
      list.push({ id: `field:${f}`, label: `${n} providers differ on ${f}`, sub: f, count: n }));
    return list;
  }, [providers]);

  const [sel, setSel] = useState("all");

  const queue = useMemo(() => {
    let items = providers.filter((p) => p.category !== "exact");
    if (sel === "only_source") items = items.filter((p) => p.status === "source_only");
    else if (sel === "only_compare") items = items.filter((p) => p.status === "target_only");
    else if (sel.startsWith("field:")) { const f = sel.slice(6); items = items.filter((p) => p.diffs?.[f]); }
    return items;
  }, [providers, sel]);

  const exportCsv = () => {
    const out = [["NPI", "Provider", "Status", "Fields differ", "Fields"]];
    queue.forEach((p) => out.push([p.npi, p.name, p.status, Object.keys(p.diffs || {}).length, Object.keys(p.diffs || {}).join("; ")]));
    downloadCsv(`review-${result.run.hash}.csv`, out);
  };

  return (
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "320px 1fr" }, gap: 2 }}>
      <Paper elevation={0} sx={{ border: "1px solid #E6E8EB", borderRadius: "8px", p: 2 }}>
        <Typography sx={{ fontSize: "0.95rem", fontWeight: 600, color: DARK }}>Why these files disagree <Box component="span" sx={{ color: MUTED, fontWeight: 400 }}>{buckets.length}</Box></Typography>
        <Typography sx={{ fontSize: "0.78rem", color: MUTED, mb: 1.5 }}>Grouped by cause, ordered by how many providers each affects.</Typography>
        <Stack spacing={1}>
          {buckets.map((b) => {
            const active = sel === b.id;
            return (
              <Paper key={b.id} elevation={0} onClick={() => setSel(b.id)} sx={{ p: 1.25, cursor: "pointer", borderRadius: "8px", border: `1px solid ${active ? DARK : "#E6E8EB"}`, backgroundColor: active ? "#FFF8F2" : "#fff", display: "flex", gap: 1.25, alignItems: "center" }}>
                <Chip label={b.count} size="small" sx={{ backgroundColor: active ? DARK : "#EEF0F2", color: active ? "#fff" : DARK, fontWeight: 600, height: 24 }} />
                <Box><Typography sx={{ fontSize: "0.82rem", color: DARK }}>{b.label}</Typography>{b.sub && <Typography sx={{ fontSize: "0.7rem", color: MUTED, fontFamily: "monospace" }}>{b.sub}</Typography>}</Box>
              </Paper>
            );
          })}
        </Stack>
      </Paper>

      <Paper elevation={0} sx={{ border: "1px solid #E6E8EB", borderRadius: "8px", p: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
          <Typography sx={{ fontSize: "0.95rem", fontWeight: 600, color: DARK }}>Work queue <Box component="span" sx={{ color: MUTED, fontWeight: 400 }}>{queue.length}</Box></Typography>
          <Button onClick={exportCsv} startIcon={<FileDownloadOutlinedIcon />} sx={{ color: DARK, border: "1px solid #E6E8EB", borderRadius: "8px", textTransform: "none", fontSize: "0.8rem" }}>Export CSV</Button>
        </Stack>
        <Typography sx={{ fontSize: "0.78rem", color: MUTED, mb: 1.5 }}>Ordered by consequence. Each row lists the fields that differ.</Typography>
        <Stack spacing={1}>
          {queue.map((p) => {
            const fields = Object.keys(p.diffs || {});
            const critical = p.realCount > 0 || p.status !== "matched";
            const desc = p.status !== "matched" ? (p.status === "source_only" ? "Only in customer" : "Only in PRIME") : `${fields.length} fields differ: ${fields.join(", ")}`;
            return (
              <Paper key={p.id} elevation={0} sx={{ p: 1.25, borderRadius: "8px", border: "1px solid #E6E8EB" }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography sx={{ fontSize: "0.88rem", fontWeight: 600, color: DARK }}>{p.name}</Typography>
                  <Typography sx={{ fontSize: "0.72rem", color: MUTED, fontFamily: "monospace" }}>NPI {p.npi}</Typography>
                  {critical && <Chip label={p.realCount > 0 ? "critical" : "review"} size="small" sx={{ height: 18, fontSize: "0.66rem", color: critical ? "#B42318" : "#B54708", backgroundColor: "#FEF3F2", border: "1px solid #FDA29B" }} />}
                </Stack>
                <Typography sx={{ fontSize: "0.78rem", color: MUTED, mt: 0.25 }}>{desc} · {p.changeType}</Typography>
              </Paper>
            );
          })}
          {queue.length === 0 && <Typography sx={{ color: MUTED, fontSize: "0.85rem", py: 3, textAlign: "center" }}>Nothing in this bucket.</Typography>}
        </Stack>
      </Paper>
    </Box>
  );
}

/* --------------------------------- Mapping ---------------------------------- */
function MappingView({ result, onTaught, onRecompare, recomputing = false }) {
  const { columns, run } = result;
  const compared = columns.filter((c) => c !== "NPI");
  // Fields that could be compared: those currently compared, plus any field some column mapped to
  // but that wasn't compared this run — so the analyst can toggle fields in/out and re-run.
  const addableFields = [...new Set((result.notComparedColumns || []).map((c) => c.field).filter(Boolean))]
    .filter((f) => f !== "NPI" && !compared.includes(f));
  const allFields = [...compared, ...addableFields];
  const [selectedFields, setSelectedFields] = useState(compared);
  // Keep the selection in sync with the run's compared fields (e.g. after a re-run).
  useEffect(() => { setSelectedFields(compared); /* eslint-disable-next-line */ }, [columns.join("|")]);
  const toggleField = (f) => setSelectedFields((prev) => prev.includes(f) ? prev.filter((x) => x !== f) : [...prev, f]);
  const selectionChanged = selectedFields.slice().sort().join("|") !== compared.slice().sort().join("|");
  // Columns taught this session are dropped from the unmapped list immediately (the teach is
  // saved server-side and is applied in full on the next comparison run).
  const [taughtHeaders, setTaughtHeaders] = useState([]);
  // Every column that wasn't compared this run — unmapped (no field) or mapped to a field outside
  // the compared set. Falls back to the older unmappedColumns shape when notComparedColumns is absent.
  const notCompared = (
    (result.notComparedColumns && result.notComparedColumns.length)
      ? result.notComparedColumns
      : (result.unmappedColumns || []).map((h) => ({ header: h, field: null }))
  ).filter((c) => !taughtHeaders.includes(c.header));
  const handleTaught = (info) => {
    if (info?.header) setTaughtHeaders((prev) => (prev.includes(info.header) ? prev : [...prev, info.header]));
    onTaught?.(info);
  };
  const panel = (label, sub) => (
    <Paper elevation={0} sx={{ border: "1px solid #E6E8EB", borderRadius: "8px", p: 2 }}>
      <Typography sx={{ fontSize: "0.9rem", fontWeight: 600, color: DARK, mb: 0.5 }}>{label}</Typography>
      <Typography sx={{ fontSize: "0.75rem", color: MUTED, mb: 1.5 }}>{sub}</Typography>
      <Table size="small" sx={{ "& td,& th": { border: 0 } }}>
        <TableHead><TableRow><TableCell><Typography sx={{ fontSize: "0.68rem", fontWeight: 600, color: MUTED, textTransform: "uppercase" }}>Compared field</Typography></TableCell><TableCell><Typography sx={{ fontSize: "0.68rem", fontWeight: 600, color: MUTED, textTransform: "uppercase" }}>How</Typography></TableCell><TableCell align="right"><Typography sx={{ fontSize: "0.68rem", fontWeight: 600, color: MUTED, textTransform: "uppercase" }}>Confidence</Typography></TableCell></TableRow></TableHead>
        <TableBody>
          {["NPI", ...compared].map((c) => (
            <TableRow key={c}>
              <TableCell sx={{ py: 0.75 }}><Typography sx={{ fontSize: "0.82rem", color: DARK }}>{c}</Typography></TableCell>
              <TableCell sx={{ py: 0.75 }}><Chip label={c === "NPI" ? "Exact" : "Dictionary"} size="small" sx={{ height: 20, fontSize: "0.72rem", backgroundColor: "#EEF2F6", color: "#475467" }} /></TableCell>
              <TableCell align="right" sx={{ py: 0.75 }}><Typography sx={{ fontSize: "0.82rem", color: DARK }}>{c === "NPI" ? "100%" : "95%"}</Typography></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Paper>
  );
  return (
    <Box>
      <Paper elevation={0} sx={{ border: "1px solid #E6E8EB", borderRadius: "8px", p: 2, mb: 2 }}>
        <Typography sx={{ fontSize: "0.85rem", color: DARK }}>
          Each file's columns are mapped onto a shared field vocabulary — the two files are never matched header-to-header. Only fields both sides carry are compared; {compared.length} fields were comparable in this run.
        </Typography>
      </Paper>
      {onRecompare && allFields.length > 0 && (
        <Paper elevation={0} sx={{ border: "1px solid #E6E8EB", borderRadius: "8px", p: 2, mb: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.75 }}>
            <Typography sx={{ fontSize: "0.9rem", fontWeight: 600, color: DARK }}>Fields compared</Typography>
            <Button
              size="small" variant="contained" disableElevation
              disabled={recomputing || !selectionChanged || selectedFields.length === 0}
              startIcon={recomputing ? <CircularProgress size={12} color="inherit" /> : null}
              onClick={() => onRecompare(selectedFields)}
              sx={{ textTransform: "none", fontSize: "0.78rem", backgroundColor: "#FF7E07", "&:hover": { backgroundColor: "#e57006" } }}
            >
              {recomputing ? "Re-running…" : "Re-run comparison"}
            </Button>
          </Stack>
          <Typography sx={{ fontSize: "0.75rem", color: MUTED, mb: 1.25 }}>
            Toggle which fields are compared, then re-run. NPI is always used to match providers.
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip label="NPI" size="small" sx={{ height: 26, fontSize: "0.75rem", backgroundColor: "#EEF2F6", color: "#475467" }} />
            {allFields.map((f) => {
              const on = selectedFields.includes(f);
              return (
                <Chip
                  key={f} label={f} size="small" onClick={() => toggleField(f)}
                  variant={on ? "filled" : "outlined"}
                  sx={{
                    height: 26, fontSize: "0.75rem", cursor: "pointer",
                    backgroundColor: on ? "#FFF3E8" : "transparent",
                    color: on ? "#B54708" : "#667085",
                    border: on ? "1px solid #FEC84B" : "1px solid #E6E8EB",
                  }}
                />
              );
            })}
          </Stack>
        </Paper>
      )}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
        {panel(run.source.title, "Source of truth")}
        {panel(run.target.title, "Compared against")}
      </Box>

      {notCompared.length > 0 && (
        <Paper elevation={0} sx={{ border: "1px solid #E6E8EB", borderRadius: "8px", p: 2, mt: 2 }}>
          <Typography sx={{ fontSize: "0.9rem", fontWeight: 600, color: DARK, mb: 0.5 }}>
            Columns not compared <Box component="span" sx={{ color: MUTED, fontWeight: 400 }}>{notCompared.length}</Box>
          </Typography>
          <Typography sx={{ fontSize: "0.75rem", color: MUTED, mb: 1.5 }}>
            Columns in <b style={{ color: DARK }}>{run.source.title}</b> that weren't compared in this run — either they matched no field, or they mapped to a field outside this comparison. Teach a column to map it — future comparisons will recognize it.
          </Typography>
          <Stack divider={<Divider />} spacing={0}>
            {notCompared.map((c) => (
              <Stack key={c.header} direction="row" alignItems="center" justifyContent="space-between" sx={{ py: 1 }}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: "0.82rem", color: DARK, fontFamily: "monospace" }} noWrap title={c.header}>{c.header}</Typography>
                  <Typography sx={{ fontSize: "0.72rem", color: MUTED }}>
                    {c.field ? `Mapped to ${c.field} · not compared in this run` : "Not compared · no matching field"}
                  </Typography>
                </Box>
                <TeachFieldPopover header={c.header} onTaught={handleTaught} />
              </Stack>
            ))}
          </Stack>
        </Paper>
      )}
    </Box>
  );
}

/* --------------------------------- Verdict ---------------------------------- */
// Make a technical change field readable: drop the DynamicCols prefix, split camelCase/underscores.
function prettyField(f) {
  let s = String(f || "").replace(/^.*DynamicCols\./i, "").replace(/^Dynamic Cols\./i, "");
  s = s.replace(/_/g, " ").replace(/([a-z0-9])([A-Z])/g, "$1 $2").trim();
  return s || String(f || "");
}

// Collect the distinct leaf fields that actually changed in a provider's change tree.
function collectChangedLeaves(changes) {
  const out = [];
  const walk = (rows) => (rows || []).forEach((r) => {
    const kids = r.childChanges || [];
    if (kids.length) walk(kids);
    else if (r.fieldName) out.push(prettyField(r.fieldName));
  });
  walk(changes);
  return [...new Set(out)];
}

// A reconciliation-style grid for a subset of "only in customer roster" providers, reused inside the
// Verdict tab. Mirrors the Reconciliation tab's "Only in customer roster" segment (single-sided source
// values, "Only in customer" chip) and carries the same expandable row drawer showing every field.
function ProviderGrid({ result, items, rowDetail }) {
  const { columns } = result;
  const dataCols = columns.filter((c) => c !== "NPI");
  const [expanded, setExpanded] = useState(() => new Set());
  const toggle = (id) => setExpanded((prev) => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });

  // The row drawer shows the provider's change history for this cycle — the same
  // renderer the Reconciliation/bucket tabs use (passed down as `rowDetail`).
  const RowDrawer = ({ p }) => (
    <TableRow>
      <TableCell colSpan={dataCols.length + 1} sx={{ backgroundColor: "#FAFBFC", borderBottom: "2px solid #EDEFF2", p: 1.5 }}>
        <Box sx={{ width: "80vw", maxWidth: "80vw", position: "sticky", left: 12 }}>
          {rowDetail
            ? rowDetail(p)
            : <Typography sx={{ fontSize: "0.82rem", color: MUTED, fontStyle: "italic" }}>No change history available for this provider.</Typography>}
        </Box>
      </TableCell>
    </TableRow>
  );

  return (
    <Box sx={{ overflowX: "auto", border: "1px solid #F0F1F3", borderRadius: "8px", mt: 1 }}>
      <Table size="small" sx={{ minWidth: 900 }}>
        <TableHead>
          <TableRow sx={{ backgroundColor: "#F7F8FA" }}>
            <TableCell sx={{ position: "sticky", left: 0, backgroundColor: "#F7F8FA", zIndex: 2, minWidth: 240 }}>
              <Typography sx={{ fontSize: "0.72rem", fontWeight: 600, color: MUTED, textTransform: "uppercase" }}>NPI / Provider</Typography>
            </TableCell>
            {dataCols.map((c) => (
              <TableCell key={c}><Typography sx={{ fontSize: "0.72rem", fontWeight: 600, color: MUTED, textTransform: "uppercase", whiteSpace: "nowrap" }}>{c}</Typography></TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((it) => {
            const p = it.p;
            const isOpen = expanded.has(p.id);
            return (
              <React.Fragment key={p.id}>
                <TableRow sx={{ "&:hover": { backgroundColor: "#FCFCFD" } }}>
                  <TableCell sx={{ verticalAlign: "top", position: "sticky", left: 0, backgroundColor: "#fff", zIndex: 1, borderBottom: "1px solid #F0F1F3", minWidth: 240 }}>
                    <Stack direction="row" spacing={0.5} alignItems="flex-start">
                      <IconButton size="small" onClick={() => toggle(p.id)} sx={{ p: 0.25, mt: "-2px" }}>
                        {isOpen ? <KeyboardArrowDownIcon sx={{ fontSize: 18, color: MUTED }} /> : <KeyboardArrowRightIcon sx={{ fontSize: 18, color: MUTED }} />}
                      </IconButton>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: "0.78rem", color: "#3E7BFA", fontWeight: 600 }}>{p.npi || "no NPI"}</Typography>
                        <Typography sx={{ fontSize: "0.85rem", color: DARK, fontWeight: 500, mb: 0.5 }}>{p.name}</Typography>
                        <Chip label="Only in customer" size="small" sx={{ height: 18, fontSize: "0.66rem", backgroundColor: TONES.sky.bg, color: TONES.sky.fg }} />
                        {it.reason ? <Typography sx={{ fontSize: "0.7rem", color: MUTED, mt: 0.5 }}>{it.reason}</Typography> : null}
                      </Box>
                    </Stack>
                  </TableCell>
                  {dataCols.map((c) => {
                    const cell = cellFor(p, c);
                    return (
                      <TableCell key={c} sx={{ py: 1, borderBottom: "1px solid #F0F1F3", verticalAlign: "top", whiteSpace: "nowrap" }}>
                        <Typography sx={{ fontSize: "0.82rem", color: cell.l ? DARK : MUTED, fontStyle: cell.l ? "normal" : "italic" }}>{cell.l || "blank"}</Typography>
                      </TableCell>
                    );
                  })}
                </TableRow>
                {isOpen && <RowDrawer p={p} />}
              </React.Fragment>
            );
          })}
          {items.length === 0 && (
            <TableRow><TableCell colSpan={dataCols.length + 1} sx={{ textAlign: "center", py: 4, border: 0 }}><Typography sx={{ color: MUTED }}>No providers.</Typography></TableCell></TableRow>
          )}
        </TableBody>
      </Table>
    </Box>
  );
}

// The Verdict tab: why does the customer roster carry providers PRIME's roster doesn't? For each
// "only in customer" provider we consult this cycle's change history and bucket it into:
//  - a rule gap (the provider changed in our system but no rule rostered it → suggest a rule),
//  - NPI not found (our system has no change record — likely a customer data/source issue), or
//  - provider not changed (no change detected this cycle, so PRIME correctly left it off).
function VerdictView({ result, historyByNpi, rowDetail }) {
  const h = historyByNpi || {};
  const addressOf = (p) => {
    const s = p.source || p.target || {};
    const l1 = [s["Address Line 1"], s["Address Line 2"]].filter(Boolean).join(", ");
    const l2 = [s["City"], s["State"], s["ZIP"] || s["ZIP Extension"]].filter(Boolean).join(" ");
    return [l1, l2].filter(Boolean).join(", ") || "—";
  };

  const buckets = useMemo(() => {
    const only = (result.providers || []).filter((p) => p.status === "source_only");
    const ruleGap = [], npiNotFound = [], notChanged = [];
    only.forEach((p) => {
      const entry = h[String(p.npi)];
      const base = { p, npi: p.npi, name: p.name, address: addressOf(p) };
      if (!entry) npiNotFound.push({ ...base, reason: "No change record found for this NPI this cycle." });
      else if (!(entry.changes && entry.changes.length)) notChanged.push({ ...base, reason: "No change detected this cycle." });
      else {
        const fields = collectChangedLeaves(entry.changes);
        const changeType = p.changeType && p.changeType !== "Full Roster" ? p.changeType : "Addition";
        // Reason is a concise label only — never the full changed-field dump (which floods the row).
        ruleGap.push({ ...base, fields, changeType, reason: entry.change || "Changed this cycle" });
      }
    });
    return { total: only.length, ruleGap, npiNotFound, notChanged };
  }, [result, historyByNpi]); // eslint-disable-line

  // Group rule-gap providers by change type + changed-field signature (input for the AI).
  const groups = useMemo(() => {
    const m = new Map();
    buckets.ruleGap.forEach((it) => {
      const sig = it.changeType + " :: " + it.fields.slice().sort().join(", ");
      if (!m.has(sig)) m.set(sig, { changeType: it.changeType, fields: it.fields, count: 0, providers: [] });
      const g = m.get(sig); g.count++; g.providers.push(it);
    });
    return [...m.values()].sort((a, b) => b.count - a.count);
  }, [buckets]);

  // AI-generated rule suggestions (one per group), with a deterministic fallback on the server.
  const [ai, setAi] = useState({ loading: false, suggestions: null });
  useEffect(() => {
    if (!groups.length) { setAi({ loading: false, suggestions: [] }); return; }
    let alive = true;
    setAi({ loading: true, suggestions: null });
    suggestRosterRulesApi(groups.map((g) => ({ changeType: g.changeType, fields: g.fields, count: g.count })))
      .then((res) => { if (alive) setAi({ loading: false, suggestions: res?.suggestions || [] }); })
      .catch(() => { if (alive) setAi({ loading: false, suggestions: [] }); });
    return () => { alive = false; };
  }, [groups]);

  const SummaryCard = ({ label, count, tone }) => (
    <Paper elevation={0} sx={{ flex: 1, minWidth: 160, border: "1px solid #E6E8EB", borderRadius: "8px", p: 1.5 }}>
      <Typography sx={{ fontSize: "1.4rem", fontWeight: 700, color: tone || DARK }}>{count}</Typography>
      <Typography sx={{ fontSize: "0.75rem", color: MUTED }}>{label}</Typography>
    </Paper>
  );

  // Providers render in the reconciliation "Only in customer roster" style, with the expandable
  // per-field drawer (see ProviderGrid). The whole grid collapses like an accordion (default hidden).
  const ProviderRows = ({ items }) => {
    const [open, setOpen] = useState(false);
    return (
      <Box sx={{ mt: 1 }}>
        <Stack direction="row" alignItems="center" spacing={0.5} onClick={() => setOpen((o) => !o)}
          sx={{ cursor: "pointer", py: 0.5, userSelect: "none", width: "fit-content" }}>
          {open ? <KeyboardArrowDownIcon sx={{ fontSize: 18, color: MUTED }} /> : <KeyboardArrowRightIcon sx={{ fontSize: 18, color: MUTED }} />}
          <Typography sx={{ fontSize: "0.78rem", fontWeight: 600, color: "#3E7BFA" }}>
            {open ? "Hide" : "Show"} {items.length} {items.length === 1 ? "provider" : "providers"}
          </Typography>
        </Stack>
        {open && <ProviderGrid result={result} items={items} rowDetail={rowDetail} />}
      </Box>
    );
  };

  if (buckets.total === 0) {
    return (
      <Paper elevation={0} sx={{ border: "1px solid #E6E8EB", borderRadius: "8px", p: 3, textAlign: "center" }}>
        <Typography sx={{ fontSize: "0.9rem", color: DARK, fontWeight: 600 }}>No providers are only in the customer roster.</Typography>
        <Typography sx={{ fontSize: "0.8rem", color: MUTED, mt: 0.5 }}>The customer roster didn't add anything PRIME's roster is missing for this run.</Typography>
      </Paper>
    );
  }

  return (
    <Box>
      <Paper elevation={0} sx={{ border: "1px solid #E6E8EB", borderRadius: "8px", p: 2, mb: 2 }}>
        <Typography sx={{ fontSize: "0.85rem", color: DARK }}>
          <b>{buckets.total}</b> providers are only in the customer roster. PRIME's roster is generated by the configured Addition/Change/Terminate rules; anything the customer added on top is analysed against this cycle's change history below — either a rule may be missing, or the difference is a data issue on the customer side.
        </Typography>
      </Paper>

      <Stack direction="row" spacing={2} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        <SummaryCard label="Possible missing rules" count={buckets.ruleGap.length} tone="#B54708" />
        <SummaryCard label="NPI not found in this cycle" count={buckets.npiNotFound.length} tone="#B42318" />
        <SummaryCard label="Provider not changed this cycle" count={buckets.notChanged.length} tone="#475467" />
      </Stack>

      {groups.length > 0 && (
        <Paper elevation={0} sx={{ border: "1px solid #E6E8EB", borderRadius: "8px", p: 2, mb: 2 }}>
          <Typography sx={{ fontSize: "0.9rem", fontWeight: 600, color: DARK, mb: 0.5 }}>Suggested rules to add (AI) <Box component="span" sx={{ color: MUTED, fontWeight: 400 }}>{groups.length}</Box></Typography>
          <Typography sx={{ fontSize: "0.75rem", color: MUTED, mb: 1.5 }}>
            These providers changed in our system this cycle but no rule rostered them. Adding a rule like the below would let PRIME match the customer's roster.
          </Typography>
          {ai.loading ? (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 1, color: MUTED }}>
              <CircularProgress size={14} /><Typography sx={{ fontSize: "0.8rem" }}>Generating rule suggestions…</Typography>
            </Stack>
          ) : (
            <Stack divider={<Divider />} spacing={0}>
              {groups.map((g, i) => {
                const s = (ai.suggestions && ai.suggestions[i]) || null;
                return (
                  <Box key={i} sx={{ py: 1.25 }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                      <Chip label={s?.changeType || g.changeType} size="small" sx={{ height: 20, fontSize: "0.66rem", fontWeight: 600, backgroundColor: "#FFF3E8", color: "#B54708", border: "1px solid #FEC84B" }} />
                      <Typography sx={{ fontSize: "0.78rem", color: MUTED }}>{g.count} {g.count === 1 ? "provider" : "providers"}</Typography>
                    </Stack>
                    <Typography sx={{ fontSize: "0.82rem", fontFamily: "monospace", color: "#2D3A45", backgroundColor: "#F7F9FA", border: "1px solid #E7EBEE", borderRadius: "6px", px: 1, py: 0.75 }}>
                      {s?.rule || `IF ${g.fields.join(", ") || "the provider"} changed THEN include as ${g.changeType}`}
                    </Typography>
                    {s?.rationale ? <Typography sx={{ fontSize: "0.75rem", color: MUTED, mt: 0.5 }}>{s.rationale}</Typography> : null}
                    <ProviderRows items={g.providers} />
                  </Box>
                );
              })}
            </Stack>
          )}
        </Paper>
      )}

      {buckets.npiNotFound.length > 0 && (
        <Paper elevation={0} sx={{ border: "1px solid #E6E8EB", borderRadius: "8px", p: 2, mb: 2 }}>
          <Typography sx={{ fontSize: "0.9rem", fontWeight: 600, color: DARK, mb: 0.5 }}>NPI not found in this cycle <Box component="span" sx={{ color: MUTED, fontWeight: 400 }}>{buckets.npiNotFound.length}</Box></Typography>
          <Typography sx={{ fontSize: "0.75rem", color: MUTED }}>Our system has no change record for these NPIs this cycle — likely missing from the source data or a customer data-prep gap.</Typography>
          <ProviderRows items={buckets.npiNotFound} />
        </Paper>
      )}

      {buckets.notChanged.length > 0 && (
        <Paper elevation={0} sx={{ border: "1px solid #E6E8EB", borderRadius: "8px", p: 2 }}>
          <Typography sx={{ fontSize: "0.9rem", fontWeight: 600, color: DARK, mb: 0.5 }}>Provider not changed this cycle <Box component="span" sx={{ color: MUTED, fontWeight: 400 }}>{buckets.notChanged.length}</Box></Typography>
          <Typography sx={{ fontSize: "0.75rem", color: MUTED }}>These providers had no detected change this cycle, so PRIME correctly left them off — the customer roster added them anyway.</Typography>
          <ProviderRows items={buckets.notChanged} />
        </Paper>
      )}
    </Box>
  );
}

/* --------------------------------- Shell ------------------------------------ */
const ComparisonResults = ({ comparison, embedded = false, rowDetail, bothShowsPrimeOnly = false, onTaught, onRecompare, recomputing = false, historyByNpi, reasonRenderer } = {}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const result = comparison || location.state?.comparison;
  // No comparison to show as a standalone page (e.g. opened directly) — send the
  // user back to start one. When embedded (with a `comparison` prop), skip this.
  useEffect(() => {
    if (!result && !embedded) navigate(ROUTE_PATHS.ROSTER_COMPARISON, { replace: true });
  }, [result, embedded, navigate]);

  const [tab, setTab] = useState("reconciliation");
  const categories = result?.categories?.length ? result.categories : ["All"];
  const [cat, setCat] = useState(categories[0]);
  const [askOpen, setAskOpen] = useState(false);
  const [askFilter, setAskFilter] = useState(null);

  const reviewCount = useMemo(() => (result?.providers || []).filter((p) => p.category !== "exact").length, [result]);
  const run = result?.run;
  const dataCols = useMemo(() => (result?.columns || []).filter((c) => c !== "NPI"), [result]);

  const handleAsk = async (question) => {
    const context = {
      source: run.source.title, target: run.target.title, asOf: run.asOf,
      activeTab: cat, categories, columns: dataCols, summary: result.summary,
    };
    const { interpretation, filter } = await askAboutRunApi(question, context);
    setAskFilter({ ...filter, _n: Date.now() });
    setTab("reconciliation");
    const scoped = categories[0] === "All" ? result.providers : result.providers.filter((p) => p.changeType === cat);
    const total = applyAskFilter(scoped, filter, dataCols, new Map()).length;
    return { interpretation, total };
  };

  const breadcrumbs = [
    { label: "Roster Comparison", link: ROUTE_PATHS.ROSTER_COMPARISON },
    { label: "Comparison Results" },
  ];

  const TABS = [
    { id: "reconciliation", label: "Reconciliation", icon: InsightsOutlinedIcon },
    // Review tab hidden for now.
    // { id: "review", label: "Review", icon: ChecklistRtlOutlinedIcon, count: reviewCount },
    { id: "mapping", label: "Mapping", icon: ViewColumnOutlinedIcon },
    { id: "verdict", label: "Verdict", icon: GavelOutlinedIcon },
  ];

  if (!result) return null; // redirecting

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, minHeight: embedded ? "auto" : "calc(100vh - 64px)", backgroundColor: embedded ? "transparent" : "primary.background", py: embedded ? 0 : 2, px: embedded ? 0 : 2 }}>
      {!embedded && <CommonBreadcrumbs breadcrumbs={breadcrumbs} />}

      {/* header */}
      {!embedded && (
      <Stack direction="row" alignItems="center" spacing={1.5} justifyContent="space-between">
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0 }}>
          <IconButton size="small" onClick={() => navigate(ROUTE_PATHS.ROSTER_COMPARISON)} sx={{ border: "1px solid #E6E8EB", backgroundColor: "#fff", borderRadius: "8px" }}>
            <ArrowBackIosNewIcon sx={{ fontSize: 16, color: DARK }} />
          </IconButton>
          <Box sx={{ width: 48, height: 48, borderRadius: "10px", backgroundColor: DARK, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <CompareArrowsOutlinedIcon sx={{ color: "#fff", fontSize: 24 }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: "1.25rem", fontWeight: 600, color: DARK, lineHeight: 1.2 }} noWrap>
              {run.source.title} <Box component="span" sx={{ color: MUTED, fontWeight: 400 }}>vs</Box> {run.target.title}
            </Typography>
            <Typography sx={{ fontSize: "0.85rem", color: "text.primary" }} noWrap>
              Compared as of {run.asOf}
            </Typography>
          </Box>
        </Stack>
        <Tooltip title={`Reproducibility hash — ${run.hash}`}>
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexShrink: 0 }}>
            <FingerprintOutlinedIcon sx={{ color: MUTED, fontSize: 18 }} />
            <Typography sx={{ fontSize: "0.78rem", color: "text.primary", fontFamily: "monospace" }}>{run.hash}</Typography>
          </Stack>
        </Tooltip>
      </Stack>
      )}

      {/* top tabs + actions */}
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center" justifyContent="space-between">
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <Button key={t.id} onClick={() => setTab(t.id)} startIcon={<Icon sx={{ fontSize: 18 }} />} sx={{
                px: 2, py: 0.75, borderRadius: "8px", textTransform: "none", fontSize: "0.9rem", fontWeight: active ? 600 : 400,
                color: active ? "#fff" : DARK, backgroundColor: active ? DARK : "#fff", border: `1px solid ${active ? DARK : "#E6E8EB"}`,
                "&:hover": { backgroundColor: active ? "#232d34" : "#F7F8FA" },
              }}>
                {t.label}{t.count != null && <Chip label={t.count} size="small" sx={{ ml: 0.75, height: 18, fontSize: "0.68rem", backgroundColor: active ? "rgba(255,255,255,0.2)" : "#EEF0F2", color: active ? "#fff" : DARK }} />}
              </Button>
            );
          })}
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          <Button onClick={() => setAskOpen(true)} startIcon={<ChatBubbleOutlineIcon sx={{ fontSize: 18 }} />} sx={{ color: DARK, border: "1px solid #E6E8EB", backgroundColor: "#fff", borderRadius: "8px", textTransform: "none", fontSize: "0.85rem", "&:hover": { backgroundColor: "#F7F8FA" } }}>Ask about this run</Button>
        </Stack>
      </Stack>

      {tab === "reconciliation" && <ReconciliationView result={result} categories={categories} cat={cat} setCat={setCat} askFilter={askFilter} rowDetail={rowDetail} bothShowsPrimeOnly={bothShowsPrimeOnly} historyByNpi={historyByNpi} reasonRenderer={reasonRenderer} />}
      {tab === "review" && <ReviewView result={result} />}
      {tab === "mapping" && <MappingView result={result} onTaught={onTaught} onRecompare={onRecompare} recomputing={recomputing} />}
      {tab === "verdict" && <VerdictView result={result} historyByNpi={historyByNpi} rowDetail={rowDetail} />}

      <AskPanel open={askOpen} onClose={() => setAskOpen(false)} onAsk={handleAsk} />
    </Box>
  );
};

export default ComparisonResults;
